<template>
  <div class="p-4 md:p-6">
    <!-- Filtri -->
    <div class="flex flex-wrap gap-3 mb-5 items-end">
      <div class="flex flex-col gap-1">
        <label class="text-xs text-gray-500 font-medium">Livello rischio</label>
        <select
          v-model="filters.min_score"
          class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
          @change="applyFilters"
        >
          <option value="31">Tutti (Medio + Alto)</option>
          <option value="61">Solo Alto (≥ 61)</option>
          <option value="0">Tutti gli utenti</option>
        </select>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs text-gray-500 font-medium">Profilo</label>
        <select
          v-model="filters.profile_type"
          class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
          @change="applyFilters"
        >
          <option value="">Tutti i profili</option>
          <option value="FOREIGN">🌍 Estero</option>
          <option value="PRIVATE">👤 Privato</option>
          <option value="BUSINESS">🏢 Azienda</option>
        </select>
      </div>

      <div class="flex flex-col gap-1 flex-1 min-w-48">
        <label class="text-xs text-gray-500 font-medium">Cerca</label>
        <input
          v-model="filters.q"
          type="text"
          placeholder="Nome, cognome o email..."
          class="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
          @keyup.enter="applyFilters"
        />
      </div>

      <div class="flex items-center gap-2 pb-2">
        <label class="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 dark:text-gray-300">
          <div
            class="relative w-10 h-5 rounded-full transition-colors duration-200"
            :class="filters.show_blocked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'"
            @click="toggleShowBlocked"
          >
            <span
              class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
              :class="filters.show_blocked ? 'translate-x-5' : 'translate-x-0'"
            ></span>
          </div>
          Mostra anche bloccati
        </label>
      </div>

      <button
        class="flex items-center gap-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition"
        @click="applyFilters"
      >
        <span class="material-icons text-sm">search</span>
        Cerca
      </button>
    </div>

    <!-- Contatore risultati -->
    <div class="flex items-center justify-between mb-3">
      <p class="text-sm text-gray-500 dark:text-gray-400">
        <span v-if="loading">Caricamento...</span>
        <span v-else>{{ total }} utenti trovati</span>
      </p>
      <div class="flex items-center gap-2 text-sm text-gray-500">
        <span>Ordina per:</span>
        <button
          :class="sortBy === 'score' ? 'text-blue-600 font-medium' : 'hover:text-blue-500'"
          @click="setSort('score')"
        >
          Rischio
        </button>
        <span>·</span>
        <button
          :class="sortBy === 'unpaid_eur' ? 'text-blue-600 font-medium' : 'hover:text-blue-500'"
          @click="setSort('unpaid_eur')"
        >
          Non pagato
        </button>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="space-y-2">
      <div
        v-for="i in 8"
        :key="i"
        class="h-16 bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse"
      ></div>
    </div>

    <!-- Tabella -->
    <div v-else-if="items.length" class="bg-white dark:bg-gray-900 rounded-xl shadow overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-gray-50 dark:bg-gray-800 text-left text-xs text-gray-500 uppercase tracking-wide">
            <th class="px-4 py-3 w-16">Rischio</th>
            <th class="px-4 py-3">Nome</th>
            <th class="px-4 py-3 hidden md:table-cell">Email</th>
            <th class="px-4 py-3 hidden lg:table-cell w-24">Profilo</th>
            <th class="px-4 py-3 hidden lg:table-cell w-24">Iscritto</th>
            <th class="px-4 py-3 w-28 text-right">Non pagato</th>
            <th class="px-4 py-3 hidden xl:table-cell">Segnali</th>
            <th class="px-4 py-3 w-28 text-right">Azioni</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
          <tr
            v-for="user in items"
            :key="user.uid"
            class="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition cursor-pointer"
            @click="goToDetail(user.uid)"
          >
            <!-- Rischio badge con tooltip -->
            <td class="px-4 py-3">
              <div class="relative group">
                <span
                  :class="scoreBadgeClass(user.score)"
                  class="inline-flex w-10 h-10 items-center justify-center rounded-full text-sm font-bold cursor-help"
                >
                  {{ user.score }}
                </span>
                <!-- Tooltip breakdown segnali -->
                <div
                  class="absolute left-12 top-0 z-50 hidden group-hover:block w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-3 text-xs"
                >
                  <p class="font-bold text-gray-800 dark:text-white mb-2">
                    Rischio {{ user.score }}/100 — {{ levelLabel(user.level) }}
                  </p>
                  <ul class="space-y-1">
                    <li
                      v-for="sig in user.signals"
                      :key="sig.code"
                      class="flex justify-between"
                      :class="sig.points > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'"
                    >
                      <span>{{ sig.points > 0 ? '▲' : '▼' }} {{ sig.label }}</span>
                      <span class="font-mono ml-2">
                        {{ sig.points > 0 ? '+' : '' }}{{ sig.points }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </td>

            <!-- Nome -->
            <td class="px-4 py-3">
              <div class="flex items-center gap-2">
                <div>
                  <p class="font-medium text-gray-900 dark:text-white">
                    {{ user.name }} {{ user.surname }}
                  </p>
                  <p class="text-xs text-gray-400 md:hidden">{{ user.email }}</p>
                  <span
                    v-if="user.in_blacklist"
                    class="text-xs bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-1.5 py-0.5 rounded"
                  >
                    Bloccato
                  </span>
                </div>
              </div>
            </td>

            <!-- Email -->
            <td class="px-4 py-3 hidden md:table-cell">
              <span class="text-gray-600 dark:text-gray-400">{{ user.email }}</span>
              <span
                v-if="isEmailSuspicious(user.email)"
                class="material-icons text-yellow-500 text-sm ml-1 align-middle"
                title="Email sospetta"
              >warning</span>
            </td>

            <!-- Profilo -->
            <td class="px-4 py-3 hidden lg:table-cell">
              <span class="text-base">{{ profileIcon(user.profile_type) }}</span>
              <span class="text-xs text-gray-500 ml-1">{{ profileLabel(user.profile_type) }}</span>
            </td>

            <!-- Iscritto -->
            <td class="px-4 py-3 hidden lg:table-cell text-gray-500 text-xs">
              {{ relativeDate(user.created_at) }}
            </td>

            <!-- Non pagato -->
            <td class="px-4 py-3 text-right">
              <span
                v-if="user.unpaid_eur > 0"
                class="font-semibold text-red-600 dark:text-red-400"
              >
                {{ formatEur(user.unpaid_eur) }}
              </span>
              <span v-else class="text-gray-300">—</span>
            </td>

            <!-- Segnali chips -->
            <td class="px-4 py-3 hidden xl:table-cell">
              <div class="flex flex-wrap gap-1">
                <span
                  v-for="sig in activePositiveSignals(user.signals).slice(0, 3)"
                  :key="sig.code"
                  class="text-xs bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 px-2 py-0.5 rounded-full"
                >
                  {{ sig.label }}
                </span>
              </div>
            </td>

            <!-- Azioni -->
            <td class="px-4 py-3 text-right" @click.stop>
              <div class="flex justify-end gap-2">
                <button
                  class="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-600 transition"
                  title="Dettaglio"
                  @click.stop="goToDetail(user.uid)"
                >
                  <span class="material-icons text-base">visibility</span>
                </button>
                <button
                  v-if="!user.in_blacklist"
                  class="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 transition"
                  title="Blocca"
                  @click.stop="openBlockModal(user)"
                >
                  <span class="material-icons text-base">block</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="bg-white dark:bg-gray-900 rounded-xl shadow p-12 text-center text-gray-400"
    >
      <span class="material-icons text-5xl mb-3 block">verified_user</span>
      <p class="text-lg font-medium">Nessun utente trovato con questi filtri.</p>
      <p class="text-sm mt-1">Prova ad abbassare la soglia di rischio o rimuovere i filtri.</p>
    </div>

    <!-- Paginazione -->
    <div v-if="total > pageSize" class="flex items-center justify-between mt-4">
      <p class="text-sm text-gray-500">
        Pagina {{ currentPage + 1 }} di {{ totalPages }}
      </p>
      <div class="flex gap-2">
        <button
          class="px-3 py-1.5 rounded border text-sm disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
          :disabled="currentPage === 0"
          @click="prevPage"
        >
          ← Precedente
        </button>
        <button
          class="px-3 py-1.5 rounded border text-sm disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
          :disabled="currentPage >= totalPages - 1"
          @click="nextPage"
        >
          Successiva →
        </button>
      </div>
    </div>

    <!-- Modal blocco rapido -->
    <flow-modal v-if="blockModal.open" @backdrop="closeBlockModal">
      <template #header>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Blocca {{ blockModal.user?.name }} {{ blockModal.user?.surname }}
        </h3>
      </template>
      <template #body>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Inserisci il motivo del blocco (minimo 5 caratteri).
        </p>
        <textarea
          v-model="blockModal.reason"
          class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm bg-white dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-red-500 focus:outline-none"
          rows="3"
          placeholder="Es: Carta condivisa con 4 account, zero pagamenti..."
        ></textarea>
      </template>
      <template #footer>
        <button
          class="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800 transition"
          @click="closeBlockModal"
        >
          Annulla
        </button>
        <button
          class="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50"
          :disabled="blockModal.reason.length < 5 || blockModal.loading"
          @click="confirmBlock"
        >
          {{ blockModal.loading ? 'Blocco in corso...' : 'Blocca utente' }}
        </button>
      </template>
    </flow-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      filters: {
        min_score: '31',
        profile_type: '',
        q: '',
        show_blocked: false,
      },
      sortBy: 'score',
      currentPage: 0,
      pageSize: 50,
      blockModal: { open: false, user: null, reason: '', loading: false },
    };
  },
  computed: {
    ...mapGetters(['antifrode_usersAtRisk', 'antifrode_loading']),
    items() {
      return this.antifrode_usersAtRisk?.items || [];
    },
    total() {
      return this.antifrode_usersAtRisk?.total || 0;
    },
    loading() {
      return this.antifrode_loading;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
  },
  mounted() {
    this.loadPage();
  },
  methods: {
    loadPage() {
      this.$store.dispatch('fetchAntifrodeUsersAtRisk', {
        min_score: this.filters.min_score,
        profile_type: this.filters.profile_type,
        q: this.filters.q,
        show_blocked: this.filters.show_blocked ? 'true' : 'false',
        sort_by: this.sortBy,
        limit: this.pageSize,
        offset: this.currentPage * this.pageSize,
      });
    },
    applyFilters() {
      this.currentPage = 0;
      this.loadPage();
    },
    toggleShowBlocked() {
      this.filters.show_blocked = !this.filters.show_blocked;
      this.applyFilters();
    },
    setSort(field) {
      this.sortBy = field;
      this.applyFilters();
    },
    prevPage() {
      if (this.currentPage > 0) {
        this.currentPage--;
        this.loadPage();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages - 1) {
        this.currentPage++;
        this.loadPage();
      }
    },
    goToDetail(uid) {
      this.$router.push({ name: 'menu.antifrode.utenti.detail', params: { uid } });
    },
    openBlockModal(user) {
      this.blockModal = { open: true, user, reason: '', loading: false };
    },
    closeBlockModal() {
      this.blockModal = { open: false, user: null, reason: '', loading: false };
    },
    async confirmBlock() {
      this.blockModal.loading = true;
      try {
        await this.$store.dispatch('antifrode_blockUser', {
          uid: this.blockModal.user.uid,
          reason: this.blockModal.reason,
        });
        this.closeBlockModal();
        this.loadPage();
      } catch (e) {
        console.error('Blocco fallito', e);
        this.blockModal.loading = false;
      }
    },
    // ── helpers UI ───────────────────────────────────────────────
    scoreBadgeClass(score) {
      if (score >= 70) return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
      if (score >= 40) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
      return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    },
    levelLabel(level) {
      if (level === 'HIGH') return 'ALTO';
      if (level === 'MEDIUM') return 'MEDIO';
      return 'BASSO';
    },
    profileIcon(type) {
      if (type === 'FOREIGN') return '🌍';
      if (type === 'BUSINESS') return '🏢';
      return '👤';
    },
    profileLabel(type) {
      if (type === 'FOREIGN') return 'Estero';
      if (type === 'BUSINESS') return 'Azienda';
      return 'Privato';
    },
    relativeDate(dateStr) {
      if (!dateStr) return '—';
      const diffMs = Date.now() - new Date(dateStr).getTime();
      const days = Math.floor(diffMs / 86400000);
      if (days === 0) return 'oggi';
      if (days === 1) return 'ieri';
      if (days < 30) return `${days} gg fa`;
      if (days < 365) return `${Math.floor(days / 30)} mesi fa`;
      return `${Math.floor(days / 365)} anni fa`;
    },
    formatEur(val) {
      if (!val) return '—';
      return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }).format(val);
    },
    isEmailSuspicious(email) {
      if (!email) return false;
      const suspiciousTlds = ['.con', '.cmo', '.cm', '.cpm', '.ocm', '.coc', '.cim'];
      if (suspiciousTlds.some((t) => email.toLowerCase().endsWith(t))) return true;
      if (email.toLowerCase().includes('@gmail.') && (email.match(/\./g) || []).length >= 5) return true;
      return false;
    },
    activePositiveSignals(signals) {
      if (!signals) return [];
      return signals.filter((s) => s.points > 0);
    },
  },
};
</script>

<style></style>
